import axios from 'axios'
import config from '../config/config'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens or other headers here if needed
    const token = sessionStorage.getItem('ticketUserToken')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response
      
      switch (status) {
        case 400:
          throw new Error(data.message || 'Bad request')
        case 401:
          throw new Error('Unauthorized')
        case 403:
          throw new Error('Forbidden')
        case 404:
          throw new Error('Not found')
        case 500:
          throw new Error('Internal server error')
        default:
          throw new Error(data.message || 'An error occurred')
      }
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your connection.')
    } else {
      // Other error
      throw new Error('An unexpected error occurred')
    }
  }
)

// API endpoints
export const eventsAPI = {
  // Get all purchasable events
  getPurchasableEvents: () => api.get('/events/purchasable'),
  
  // Get event details by ID
  getEventById: (eventId) => api.get(`/events/${eventId}`),
  
  // Get event pricing (early/late fees)
  getEventPricing: (eventId) => api.get(`/events/${eventId}/pricing`),
}

export const ticketsAPI = {
  // Send email verification code
  sendVerificationCode: (email, eventId, quantity) => 
    api.post('/tickets/verify-email', { email, eventId, quantity }),
  
  // Verify email code
  verifyEmailCode: (email, code) => 
    api.post('/tickets/verify-code', { email, code }),
  
  // Create ticket order
  createOrder: (orderData) => 
    api.post('/tickets/orders', orderData),
  
  // Get order details
  getOrder: (orderId) => 
    api.get(`/tickets/orders/${orderId}`),
  
  // Process payment
  processPayment: (orderId, paymentData) => 
    api.post(`/tickets/orders/${orderId}/payment`, paymentData),
  
  // Get ticket downloads as PDF (binary)
  getTicketDownloads: async (orderId) => {
    const response = await api.get(`/tickets/orders/${orderId}/downloads`, {
      responseType: 'blob'
    })
    return response
  },

  // Get receipt PDF for a payment (binary)
  getReceipt: async (paymentId, email, locale) => {
    const response = await api.get(`/tickets/receipt/${paymentId}?email=${encodeURIComponent(email)}&locale=${encodeURIComponent(locale)}` , {
      responseType: 'blob'
    })
    return response
  },

  // Get tickets by user email (to check existing registrations)
  getUserTicketsByEmail: (email) => 
    api.post('/tickets/user', { identifier: email, type: 'email' }),
}

export const paymentAPI = {
  // Generate Redsys payment form data
  generateRedsysPayment: (orderId) => 
    api.post(`/payment/redsys/generate`, { orderId }),
  
  // Handle Redsys callback
  handleRedsysCallback: (callbackData) => 
    api.post('/payment/redsys/callback', callbackData),
}

export default api
